import React, { useState, useEffect } from 'react'
import { Sidebar } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { Heading, Text, Button, Input, Select, LoadingWithRetry, LoadingSpinner } from '../components/ui'
import { BRAND_COLOR } from '../constants'
import { supabase } from '../lib/supabase'
// import { useSupabaseTable } from '../hooks'
import {
  MagnifyingGlassIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ChatBubbleLeftRightIcon,
  TagIcon
} from '@heroicons/react/24/outline'

interface SilviaUser {
  id: number
  created_at: string
  name: string
  whatsapp: string
}

interface Chat {
  id: number
  name: string
  lastMessage: string
  time: string
  unread: boolean
  muted: boolean
  avatar: string
  participants?: string[]
  category: string
  status: 'ativo' | 'resolvido' | 'pendente' | 'escalado'
  priority: 'baixa' | 'média' | 'alta'
  satisfaction?: number
  tags: string[]
}

interface SilviaMessage {
  id: number
  created_at: string
  user_id: number
  message_user?: string
  message_ai?: string
  protocol?: string
}

interface Message {
  id: number
  sender: string
  content: string
  time: string
  isOutgoing: boolean
}


const SilvIA: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedPriority, setSelectedPriority] = useState('all')
  const [selectedUser, setSelectedUser] = useState('all')
  const [activeTab, setActiveTab] = useState<'conversas' | 'graficos'>('conversas')
  const [silviaMessages, setSilviaMessages] = useState<SilviaMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)

  // Contadores globais de mensagens
  const [totalMessagesCount, setTotalMessagesCount] = useState(0)
  const [totalAiResponsesCount, setTotalAiResponsesCount] = useState(0)
  const [totalUserMessagesCount, setTotalUserMessagesCount] = useState(0)

  // Estados para carregamento
  const [silviaUsers, setSilviaUsers] = useState<SilviaUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  // Carregar usuários do SilvIA do Supabase com retry
  useEffect(() => {
    const fetchSilviaUsers = async (attempt: number = 0) => {
      try {
        setLoading(true)
        setError(null)

        // Buscar todos os registros usando paginação
        const allData: SilviaUser[] = []
        const batchSize = 1000
        let from = 0
        let hasMore = true

        while (hasMore) {
          const { data, error } = await supabase
            .from('silvia_users')
            .select('*')
            .not('whatsapp', 'is', null)
            .neq('whatsapp', '')
            .order('created_at', { ascending: false })
            .range(from, from + batchSize - 1)

          if (error) {
            throw error
          }

          if (data && data.length > 0) {
            allData.push(...(data as SilviaUser[]))
            
            // Se retornou menos que o batchSize, não há mais dados
            if (data.length < batchSize) {
              hasMore = false
            } else {
              from += batchSize
            }
          } else {
            hasMore = false
          }
        }

        setSilviaUsers(allData)
        setRetryCount(0)
      } catch (err: any) {
        console.error(`Tentativa ${attempt + 1} falhou:`, err)
        
        if (attempt < 2) { // 3 tentativas total
          setRetryCount(attempt + 1)
          setTimeout(() => {
            fetchSilviaUsers(attempt + 1)
          }, 1000 * Math.pow(2, attempt)) // Backoff exponencial
        } else {
          setError(err.message || 'Erro ao carregar usuários do SilvIA')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchSilviaUsers()
  }, [])

  // Carregar contadores globais de mensagens
  useEffect(() => {
    const fetchGlobalMessageCounts = async () => {
      try {
        const { count: ai } = await supabase
          .from('silvia_messages')
          .select('*', { count: 'exact', head: true })
          .not('message_ai', 'is', null)
          .neq('message_ai', '')

        const { count: user } = await supabase
          .from('silvia_messages')
          .select('*', { count: 'exact', head: true })
          .not('message_user', 'is', null)
          .neq('message_user', '')

        // Total geral = enviados (user) + recebidos (ai)
        setTotalMessagesCount((ai || 0) + (user || 0))
        setTotalAiResponsesCount(ai || 0)
        setTotalUserMessagesCount(user || 0)
      } catch (err) {
        console.error('Erro ao carregar contadores globais do SilvIA:', err)
      }
    }

    fetchGlobalMessageCounts()
  }, [])

  const refetchUsers = async () => {
    setRetryCount(0)
    setError(null)
    setLoading(true)
    
    try {
      // Buscar todos os registros usando paginação
      const allData: SilviaUser[] = []
      const batchSize = 1000
      let from = 0
      let hasMore = true

      while (hasMore) {
        const { data, error } = await supabase
          .from('silvia_users')
          .select('*')
          .not('whatsapp', 'is', null)
          .neq('whatsapp', '')
          .order('created_at', { ascending: false })
          .range(from, from + batchSize - 1)

        if (error) {
          throw error
        }

        if (data && data.length > 0) {
          allData.push(...(data as SilviaUser[]))
          
          // Se retornou menos que o batchSize, não há mais dados
          if (data.length < batchSize) {
            hasMore = false
          } else {
            from += batchSize
          }
        } else {
          hasMore = false
        }
      }

      setSilviaUsers(allData)
    } catch (err: any) {
      setError(err.message || 'Erro ao recarregar usuários')
    } finally {
      setLoading(false)
    }
  }

  // Carregar mensagens quando um chat for selecionado
  const fetchMessages = async (userId: number) => {
    try {
      setMessagesLoading(true)
      const { data, error } = await supabase
        .from('silvia_messages')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true })

      if (error) {
        throw error
      }

      setSilviaMessages(data || [])
    } catch (err: any) {
      console.error('Erro ao carregar mensagens:', err.message)
    } finally {
      setMessagesLoading(false)
    }
  }


  // Dados de análise
  const conversationData = [
    { day: '01/01', volume: 45, satisfaction: 4.2 },
    { day: '02/01', volume: 52, satisfaction: 4.5 },
    { day: '03/01', volume: 38, satisfaction: 4.0 },
    { day: '04/01', volume: 67, satisfaction: 4.3 },
    { day: '05/01', volume: 89, satisfaction: 4.7 },
    { day: '06/01', volume: 76, satisfaction: 4.8 },
    { day: '07/01', volume: 94, satisfaction: 4.6 },
    { day: '08/01', volume: 112, satisfaction: 4.9 },
    { day: '09/01', volume: 98, satisfaction: 4.7 },
    { day: '10/01', volume: 125, satisfaction: 4.5 }
  ]

  const topCategories = [
    { name: 'Suporte Técnico', count: 45, percentage: 30 },
    { name: 'Dúvidas Gerais', count: 38, percentage: 25 },
    { name: 'Relatórios', count: 32, percentage: 21 },
    { name: 'Configurações', count: 25, percentage: 17 },
    { name: 'Outros', count: 10, percentage: 7 }
  ]

  // Converter usuários do Supabase em chats
  const convertUsersToChats = (users: SilviaUser[]): Chat[] => {
    const categories = ['Suporte Técnico', 'Dúvidas Gerais', 'Relatórios', 'Configurações', 'Outros']
    const statuses: ('ativo' | 'resolvido' | 'pendente' | 'escalado')[] = ['ativo', 'resolvido', 'pendente', 'escalado']
    const priorities: ('baixa' | 'média' | 'alta')[] = ['baixa', 'média', 'alta']
    const avatars = ['👩', '👨', '👩', '👨', '👩', '👨', '👩', '👨', '👩', '👨']
    
    return users.map((user, index) => {
      // Usar ID do usuário para gerar valores determinísticos
      const userHash = user.id % 100
      const categoryIndex = userHash % categories.length
      const statusIndex = (userHash + 1) % statuses.length
      const priorityIndex = (userHash + 2) % priorities.length
      
      const category = categories[categoryIndex]
      const status = statuses[statusIndex]
      const priority = priorities[priorityIndex]
      const avatar = avatars[index % avatars.length]
      
      return {
        id: user.id,
        name: user.name,
        lastMessage: `Conversa iniciada via WhatsApp: ${user.whatsapp}`,
        time: new Date(user.created_at).toLocaleTimeString('pt-BR', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        unread: (userHash % 5) > 0, // true/false baseado no ID
        muted: userHash % 10 > 8, // 20% chance baseada no ID
        avatar: avatar,
        participants: [user.name, 'SilvIA'],
        category: category,
        status: status,
        priority: priority,
        satisfaction: (userHash % 3) + 3, // 3-5 baseado no ID
        tags: [category.toLowerCase(), 'whatsapp', 'silvia']
      }
    })
  }

  const chats = convertUsersToChats(silviaUsers)
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null)

  const categories = ['all', 'Suporte Técnico', 'Dúvidas Gerais', 'Relatórios', 'Configurações', 'Outros']
  const statuses = ['all', 'ativo', 'resolvido', 'pendente', 'escalado']
  const priorities = ['all', 'baixa', 'média', 'alta']
  const users = ['all', ...silviaUsers.map(user => user.name)]

  // Filtros aplicados
  const filteredChats = chats.filter(chat => {
    const matchSearch = chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       chat.lastMessage.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       chat.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    
    const matchCategory = selectedCategory === 'all' || chat.category === selectedCategory
    const matchStatus = selectedStatus === 'all' || chat.status === selectedStatus
    const matchPriority = selectedPriority === 'all' || chat.priority === selectedPriority
    const matchUser = selectedUser === 'all' || chat.name === selectedUser

    return matchSearch && matchCategory && matchStatus && matchPriority && matchUser
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'resolvido': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'pendente': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'escalado': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'alta': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'média': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'baixa': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }


  const getMessagesForChat = (chatId: number): Message[] => {
    // Converter mensagens do Supabase para o formato da interface
    const messages: Message[] = []
    
    silviaMessages.forEach((msg, index) => {
      // Adicionar mensagem do usuário se existir
      if (msg.message_user) {
        const msgDate = new Date(msg.created_at)
        messages.push({
          id: msg.id * 2, // ID único para mensagem do usuário
          sender: silviaUsers.find(user => user.id === msg.user_id)?.name || 'Usuário',
          content: msg.message_user,
          time: `${msgDate.toLocaleDateString('pt-BR')} ${msgDate.toLocaleTimeString('pt-BR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}`,
          isOutgoing: false
        })
      }
      
      // Adicionar mensagem da IA se existir
      if (msg.message_ai) {
        const msgDate = new Date(msg.created_at)
        messages.push({
          id: msg.id * 2 + 1, // ID único para mensagem da IA
          sender: 'SilvIA',
          content: msg.message_ai,
          time: `${msgDate.toLocaleDateString('pt-BR')} ${msgDate.toLocaleTimeString('pt-BR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}`,
          isOutgoing: true
        })
      }
    })
    
    return messages.sort((a, b) => a.id - b.id)
  }

  return (
    <LoadingWithRetry
      loading={loading}
      error={error}
      retryCount={retryCount}
      onRetry={refetchUsers}
      loadingMessage="Carregando conversas do SilvIA..."
      errorMessage="Erro ao carregar conversas do SilvIA"
    >
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
          <PermissionGuard 
            moduleName="atendimentos_silvia" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar esta página.
                  </Text>
                </div>
              </div>
            }
          >
              <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                SilvIA
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                Interface de conversação inteligente com o SilvIA
              </Text>
            </div>

            {/* Topo Colorido - Valores */}
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-2">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {silviaUsers.length}
                    </Text>
                    <Text size="sm" className="text-white">
                      Total de Usuários
                    </Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {totalMessagesCount}
                    </Text>
                    <Text size="sm" className="text-green-100">
                      Total de Mensagens
                    </Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {totalAiResponsesCount}
                    </Text>
                    <Text size="sm" className="text-purple-100">
                      Respostas da IA
                    </Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">
                      {totalUserMessagesCount}
                    </Text>
                    <Text size="sm" className="text-orange-100">
                      Mensagens dos Usuários
                    </Text>
                  </div>
                </div>
              </div>
            </div>

            

            {/* Filtros Avançados */}
            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Filtros
                  </Heading>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                  <div className="md:col-span-4">
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Buscar
                    </Text>
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Buscar conversas..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  {true && (
                    <>
                      <div className="md:col-span-2">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                          Categoria
                        </Text>
                        <Select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                          {categories.map(category => (
                            <option key={category} value={category}>
                              {category === 'all' ? 'Todas as categorias' : category}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                          Status
                        </Text>
                        <Select
                          value={selectedStatus}
                          onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                          {statuses.map(status => (
                            <option key={status} value={status}>
                              {status === 'all' ? 'Todos os status' : status}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                          Prioridade
                        </Text>
                        <Select
                          value={selectedPriority}
                          onChange={(e) => setSelectedPriority(e.target.value)}
                        >
                          {priorities.map(priority => (
                            <option key={priority} value={priority}>
                              {priority === 'all' ? 'Todas as prioridades' : priority}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                          Usuário
                        </Text>
                        <Select
                          value={selectedUser}
                          onChange={(e) => setSelectedUser(e.target.value)}
                        >
                          {users.map(user => (
                            <option key={user} value={user}>
                              {user === 'all' ? 'Todos os usuários' : user}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </>
                  )}
                  
                  <div className="md:col-span-2 flex justify-start">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSearchQuery('')
                        setSelectedCategory('all')
                        setSelectedStatus('all')
                        setSelectedPriority('all')
                        setSelectedUser('all')
                      }}
                    >
                      Limpar Filtros
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção de Abas */}
            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                {/* Navegação das Abas */}
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="flex space-x-8 px-6">
                    <button
                      onClick={() => setActiveTab('conversas')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'conversas'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <ChatBubbleLeftRightIcon className="h-5 w-5" />
                        <span>Conversas</span>
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('graficos')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'graficos'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <ChartBarIcon className="h-5 w-5" />
                        <span>Gráficos</span>
                      </div>
                    </button>
                  </nav>
                </div>

                {/* Conteúdo das Abas */}
                <div className="p-6">
                  {activeTab === 'conversas' && (
                    <div className="h-[600px] flex bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                      {/* Coluna da lista de chats */}
                      <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col">
                        {/* Header */}
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-4">
                            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                              Conversas ({filteredChats.length})
                            </Heading>
                          </div>
                        </div>

                        {/* Lista de chats */}
                        <div className="flex-1 overflow-y-auto max-h-[500px]">
                          {filteredChats.map((chat) => (
                            <div
                              key={chat.id}
                              onClick={() => {
                                setSelectedChat(chat)
                                fetchMessages(chat.id)
                              }}
                              className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-colors ${
                                selectedChat?.id === chat.id
                                  ? 'bg-gray-100 dark:bg-gray-700'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                  <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-lg">
                                    {chat.avatar}
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <Text 
                                      size="sm" 
                                      className="font-semibold truncate text-left"
                                      style={{ color: BRAND_COLOR }}
                                    >
                                      {chat.name}
                                    </Text>
                                    <div className="flex items-center space-x-1">
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(chat.priority)}`}>
                                        {chat.priority}
                                      </span>
                                      <Text size="xs" className="text-gray-500 dark:text-gray-400">
                                        {chat.time}
                                      </Text>
                                    </div>
                                  </div>
                                  <Text size="sm" className={`truncate text-left mb-2 ${
                                    chat.unread 
                                      ? 'text-gray-900 dark:text-white font-medium' 
                                      : 'text-gray-500 dark:text-gray-400'
                                  }`}>
                                    {chat.lastMessage}
                                  </Text>
                                  <div className="flex items-center justify-between">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(chat.status)}`}>
                                      {chat.status}
                                    </span>
                                    <div className="flex items-center space-x-1">
                                      <TagIcon className="h-3 w-3 text-gray-400" />
                                      <Text size="xs" className="text-gray-500 dark:text-gray-400">
                                        {chat.category}
                                      </Text>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Coluna das mensagens */}
                      <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-900">
                        {selectedChat ? (
                          <>
                            {/* Header do chat */}
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                              <div className="flex items-center">
                                <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-lg mr-3">
                                  {selectedChat.avatar}
                                </div>
                                <div>
                                  <Text size="sm" className="font-semibold text-gray-900 dark:text-white" style={{ textAlign: 'left' }}>
                                    {selectedChat.name}
                                  </Text>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedChat.status)}`}>
                                      {selectedChat.status}
                                    </span>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedChat.priority)}`}>
                                      {selectedChat.priority}
                                    </span>
                                    <Text size="xs" className="text-gray-500 dark:text-gray-400">
                                      {selectedChat.category}
                                    </Text>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Área das mensagens */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[500px]">
                              {messagesLoading ? (
                                <div className="flex items-center justify-center h-32">
                                  <div className="text-center">
                                    <LoadingSpinner size="md" className="mx-auto mb-2" />
                                    <Text size="sm" className="text-gray-500 dark:text-gray-400">
                                      Carregando mensagens...
                                    </Text>
                                  </div>
                                </div>
                              ) : (
                                getMessagesForChat(selectedChat.id).map((message) => (
                                <div
                                  key={message.id}
                                  className={`flex ${message.isOutgoing ? 'justify-end' : 'justify-start'}`}
                                >
                                  <div
                                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                      message.isOutgoing
                                        ? 'text-white'
                                        : 'text-white'
                                    }`}
                                    style={{
                                      backgroundColor: message.isOutgoing ? BRAND_COLOR : '#9ca3af'
                                    }}
                                  >
                                    <div className={`flex items-center justify-between mb-1 ${
                                      message.isOutgoing ? 'flex-row-reverse' : 'flex-row'
                                    }`}>
                                      <Text 
                                        size="xs" 
                                        className="font-medium text-left"
                                        style={{ color: message.isOutgoing ? '#ffffff' : '#ffffff' }}
                                      >
                                        {message.sender}
                                      </Text>
                                      <Text size="xs" className={`${
                                        message.isOutgoing ? 'text-white' : 'text-gray-500 dark:text-gray-400'
                                      }`}>
                                        {message.time}
                                      </Text>
                                    </div>
                                    <Text size="sm" className={`${
                                      message.isOutgoing ? 'text-right' : 'text-left'
                                    }`} style={{ color: message.isOutgoing ? '#ffffff' : '#000000' }}>
                                      {message.content}
                                    </Text>
                                  </div>
                                </div>
                              ))
                              )}
                            </div>
                          </>
                        ) : (
                          /* Estado vazio quando nenhum chat está selecionado */
                          <div className="flex-1 flex items-center justify-center">
                            <div className="text-center">
                              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                <span className="text-2xl">💬</span>
                              </div>
                              <Text size="lg" className="text-gray-900 dark:text-white mb-2 font-semibold">
                                Selecione um chat
                              </Text>
                              <Text size="sm" color="muted" className="max-w-md">
                                Escolha um contato da lista para visualizar a conversa.
                              </Text>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'graficos' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Gráfico de Volume */}
                      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                          Volume de Conversas (Últimos 10 dias)
                        </Heading>
                        <div className="h-64 flex items-end justify-between space-x-2">
                          {conversationData.map((item, index) => (
                            <div key={index} className="flex-1 flex flex-col items-center">
                              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-t h-48 mb-2 relative">
                                <div 
                                  className="absolute bottom-0 w-full rounded-t"
                                  style={{ 
                                    height: `${(item.volume / 125) * 100}%`,
                                    backgroundColor: BRAND_COLOR 
                                  }}
                                ></div>
                              </div>
                              <Text size="xs" className="text-gray-500 dark:text-gray-400 text-center">
                                {item.day}
                              </Text>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Top Categorias */}
                      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                          Top Categorias
                        </Heading>
                        <div className="space-y-4">
                          {topCategories.map((category, index) => (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div 
                                  className="w-4 h-4 rounded"
                                  style={{ backgroundColor: BRAND_COLOR }}
                                ></div>
                                <Text size="sm" className="text-gray-900 dark:text-white">
                                  {category.name}
                                </Text>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Text size="sm" className="text-gray-500 dark:text-gray-400">
                                  {category.count}
                                </Text>
                                <Text size="xs" className="text-gray-400 dark:text-gray-500">
                                  ({category.percentage}%)
                                </Text>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

              </div>
            </PermissionGuard>
        </div>
      </div>
    </LoadingWithRetry>
  )
}

export default SilvIA
